import React, { useEffect, useState, useContext } from "react";
import { CurrencyExchange } from "@mui/icons-material";
import { debounce } from "lodash";
import {
  criarAjusteManualVerba,
  obterVerbasAjustadas,
  obterVerbasAjustadasPorCampoDeBusca,
} from "../../services/api-esocial.service";
import TableCardv2 from "../../components/TableCardv2";
import { Autocomplete, Checkbox, TextField } from "@mui/material";
import BasicSelect from "../../components/BasicSelect";
import SnackBar from "../../components/SnackBar";
import { PreviaInssContext } from "../PreviaINSS/PreviaInssContext";
import styles from "./styles.module.css";

const AjustesVerbas = ({ data }) => {
  const [loading, setLoading] = useState(false);
  const [idGrupoEconomico, setIdGrupoEconomico] = useState(null);
  const [idEmpresa, setIdEmpresa] = useState(null);
  const [dataTable, setDataTable] = useState([]);
  const [totalData, setTotalData] = useState([]);
  const [campoPesquisa, setCampoPesquisa] = useState("Código da rúbrica");
  const { dados } = useContext(PreviaInssContext);

  const header = {
    icon: <CurrencyExchange />,
    title: "Ajuste de Verbas",
  };

  const Complemento = () => {
    const camposPesquisa = ["Código da rúbrica", "Descrição da rúbrica"];
    const styleAutoComplete = {
      maxWidth: "20rem",
      minWidth: "200px",
      "& .MuiOutlinedInput-root": {
        borderRadius: "50px",
        "& fieldset": {
          borderColor: "#18316e",
        },
        "&:hover fieldset": {
          borderColor: "#18316e",
        },
        "&.Mui-focused fieldset": {
          borderColor: "#18316e",
          maxHeight: "5rem",
          overflow: "scroll",
        },
      },
    };

    const handleChangeCampoPesquisa = (value) => {
      setCampoPesquisa(value);
    };

    return (
      <>
        <div className={styles.containerHeader}>
          <Autocomplete
            value={campoPesquisa}
            sx={styleAutoComplete}
            disableClearable
            limitTags={1}
            id="tags-outlined"
            options={camposPesquisa}
            onChange={(_, value) => handleChangeCampoPesquisa(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                variant="outlined"
                label="Coluna de pesquisa"
              />
            )}
          />
        </div>
      </>
    );
  };

  const columns = [
    { key: "inicioValidade", label: "Início da validade" },
    { key: "fimValidade", label: "Fim (calculado) da validade" },
    { key: "codigoRubrica", label: "Código da Rúbrica" },
    { key: "descricaoRubrica", label: "Descrição" },
    { key: "tipoRubrica", label: "Tipo da Rúbrica" },
    { key: "tagIncidencia", label: "Incidência" },
    { key: "tagComposicaoProjeto", label: "Composição do Projeto" },
  ];

  const valuesIncidencia = ["Sim", "Não", "Nunca"];

  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        if (idGrupoEconomico !== null && idEmpresa !== null) {
          const response = await obterVerbasAjustadas(idEmpresa.id);
          setTotalData(handleOrganizeData(response.verbas));
          setDataTable(handleOrganizeData(response.verbas));
        } else if (data) {
          const response = await obterVerbasAjustadas(data.idEmpresa);
          setTotalData(handleOrganizeData(response.verbas));
          setDataTable(handleOrganizeData(response.verbas));
        } else {
          setDataTable([]);
        }
      } catch (error) {
        setObjSnackBar({
          type: "error",
          msg: "Ops, temos um erro! Contate o suporte.",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [idEmpresa]);

  // helper to show not-editable message
  const showNotEditableMessage = () => {
    setObjSnackBar({ type: "error", msg: "Esta versão não permite alterações." });
  };

  const handleOrganizeData = (response) => {
    return response.map((item, index) => ({
      ...item,
      tagComposicaoProjeto: (
        <Checkbox
          onChange={(e) => {
            if (!dados.isEditable) {
              showNotEditableMessage();
              return;
            }
            handleChangeRow(
              response,
              index,
              "composicaoProjeto",
              e.target.checked
            );
          }}
          checked={item.composicaoProjeto}
          disabled={!dados.isEditable}
        />
      ),
      tagIncidencia: (
        <BasicSelect
          options={valuesIncidencia}
          onChange={(e) => {
            if (!dados.isEditable) {
              showNotEditableMessage();
              return;
            }
            handleChangeRow(response, index, "incidencia", e.target.value);
          }}
          value={item.incidencia}
          disabled={!dados.isEditable}
        />
      ),
      style: item.composicaoProjeto ? {} : { color: "red" },
    }));
  };

  const handleDebounce = async (value) => {
    setLoading(true);
    if (!idEmpresa && !data) {
      setObjSnackBar({
        type: "error",
        msg: "Selecione uma empresa!",
      });
      return;
    }

    if (value) {
      const tipoPesquisa =
        campoPesquisa === "Código da rúbrica" ? "codigo" : "descricao";
      const response = await obterVerbasAjustadasPorCampoDeBusca(
        data ? data.idEmpresa : idEmpresa.id,
        value,
        tipoPesquisa
      );
      setDataTable(handleOrganizeData(response.verbas));
    } else {
      if (idGrupoEconomico !== null && idEmpresa !== null && data) {
        setDataTable(handleOrganizeData(totalData));
      } else {
        setDataTable([]);
      }
    }
    setLoading(false);
  };

  const functionDebounce = debounce((value) => handleDebounce(value), 1000);

  const handleBuscaEmpresas = (obj) => {
    setIdGrupoEconomico(obj.GE);
    setIdEmpresa(obj.CNPJ);
  };

  const handleChangeRow = async (response, index, key, value) => {
    if (!dados.isEditable) {
      showNotEditableMessage();
      return;
    }

    const lista = response;
    lista[index] = { ...lista[index], [key]: value };
    if (key === "incidencia" || key === "composicaoProjeto") {
      //  Altera a incidencia pela API
      const payloadVerba = {
        ajusteVerba: {
          idTabelaRubrica: lista[index].idTabelaRubrica,
          codRubrica: lista[index].codigoRubrica,
          tipoRubrica: lista[index].tipoRubrica,
          descricaoRubrica: lista[index].descricaoRubrica,
          inicioValidade: lista[index].inicioValidade,
          tipoIncidenciaVerba:
            lista[index].incidencia === "Sim"
              ? 1
              : lista[index].incidencia === "Não"
              ? 2
              : lista[index].incidencia === "Nunca"
              ? 3
              : null,
          composicao: lista[index].composicaoProjeto,
        },
      };
      try {
        await criarAjusteManualVerba(
          data ? data.idEmpresa : idEmpresa.id,
          payloadVerba
        );
      } catch (error) {
        console.log(error);
        setObjSnackBar({
          type: "error",
          msg: "Erro ao ajustar verba, contate o suporte.",
        });
        return;
      }
    }
    setObjSnackBar({
      type: "success",
      msg: "Verba alterada com sucesso.",
    });

    setDataTable(handleOrganizeData(lista));
  };

  return (
    <>
      <TableCardv2
        header={header}
        handleGrupoEmpresa={data ? null : handleBuscaEmpresas}
        complementaryHeaderTop={<Complemento />}
        hasEmpresa={data ? false : true}
        uniqueEmpresa
        columns={columns}
        data={dataTable}
        debounce={functionDebounce}
        loading={loading}
      />
      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />
    </>
  );
};

export default AjustesVerbas;
